import * as React from 'react';
import styled from 'styled-components';

import { SessionContext } from 'src/Views/SessionContext';

import Button from 'src/SharedComponents/Button';
import Checkbox from 'src/SharedComponents/Checkbox';
import Dialog from 'src/SharedComponents/Dialog/Dialog';
import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import TextInput from 'src/SharedComponents/TextInput';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';

import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { createAgent } from './agentManagementService';

const FieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
`;

const FieldLine = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
`;

const CheckboxRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin: 0 10px 20px 10px;
    cursor: pointer;
`;

const CheckboxLabel = styled.span`
    font-size: 13px;
    color: #555;
    user-select: none;
`;

const ReadOnlyField = styled.div`
    display: flex;
    flex-direction: column;
    margin: 10px 10px 20px 10px;
    flex: 1;
`;

const ReadOnlyLabel = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    color: #888;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

const ReadOnlyValue = styled.div`
    font-size: 13px;
    color: #333;
    background: #f4f4f4;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 8px 10px;
    word-break: break-all;
    font-family: monospace;
    width: 80%;
`;

const CopyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const CopyIcon = styled.span`
    cursor: pointer;
    color: #888;
    transition: color 0.15s;
    flex-shrink: 0;

    &:hover {
        color: #187fba;
    }
`;

const StatusContainer = styled.div`
    height: 70px;
    text-align: center;
`;

const MainContainer = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    flex-wrap: nowrap;
    align-content: space-around;
    justify-content: center;
    align-items: center;
`;

const SuccessDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px;
    width: 100%;
`;

const SuccessMessage = styled.div`
    text-align: center;
    margin-bottom: 8px;
    font-weight: 500;
`;

const WarningMessage = styled.div`
    font-size: 13px;
    color: #856404;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 10px 12px;
    margin-bottom: 8px;
    align-self: center;
    width: fit-content;
`;

const generateClientId = (): string => {
    return crypto.randomUUID();
};

const generateSecret = (): string => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => chars[b % chars.length]).join("");
};

interface IProps {
    show: boolean,
    onClose: () => void,
    onConfirmAgentAdd: () => void
}

const CreateAgentDialog = (props: IProps) => {
    const { webToken } = React.useContext(SessionContext).state;
    const [nameValue, setNameValue] = React.useState("");
    const [autoGenerate, setAutoGenerate] = React.useState(true);
    const [clientIdValue, setClientIdValue] = React.useState(() => generateClientId());
    const [secretValue, setSecretValue] = React.useState(() => generateSecret());
    const [manualClientId, setManualClientId] = React.useState("");
    const [manualSecret, setManualSecret] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [success, setSuccess] = React.useState(false);
    const [copiedClientId, setCopiedClientId] = React.useState(false);
    const [copiedSecret, setCopiedSecret] = React.useState(false);

    const copyToClipboard = (value: string, setCopied: (v: boolean) => void) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const toggleAutoGenerate = (checked: boolean) => {
        setAutoGenerate(checked);
        setError("");
        if (checked) {
            setClientIdValue(generateClientId());
            setSecretValue(generateSecret());
        }
    };

    const closeDialog = () => {
        setNameValue("");
        setAutoGenerate(true);
        setClientIdValue(generateClientId());
        setSecretValue(generateSecret());
        setManualClientId("");
        setManualSecret("");
        setError("");
        if (success) {
            props.onConfirmAgentAdd();
            setSuccess(false);
        }
        props.onClose();
    };

    const addAgentConfirm = () => {
        if (!nameValue) {
            setError("Name cannot be empty.");
            return;
        }
        if (!autoGenerate && (!manualClientId || !manualSecret)) {
            setError("Client ID and Secret cannot be empty.");
            return;
        }
        const effectiveClientId = autoGenerate ? clientIdValue : manualClientId;
        const effectiveSecret = autoGenerate ? secretValue : manualSecret;
        setLoading(true);
        createAgent(webToken, { name: nameValue, clientId: effectiveClientId, secret: effectiveSecret },
            () => {
                setLoading(false);
                setSuccess(true);
                setError("");
                if (!autoGenerate) {
                    setClientIdValue(manualClientId);
                    setSecretValue(manualSecret);
                }
            },
            (errorMessage) => {
                setLoading(false);
                setError(errorMessage);
                setSuccess(false);
            });
    };

    return <Dialog open={props.show} onClose={closeDialog} style={{ width: "650px", height: "600px" }} darkBackground={true} preventClickOutsideClose={true}>
        <ManagerTitleBar viewName="Create Agent" viewDescription="Create a new agent." />
        <LoadingIndicator show={loading} type="Linear" />
        {!success && <FieldsContainer>
            <FieldLine>
                <TextInput margin="10px 10px 20px 10px" value={nameValue} onChange={(e) => setNameValue(e.currentTarget.value)} label="Name" />
            </FieldLine>
            <CheckboxRow onClick={() => toggleAutoGenerate(!autoGenerate)}>
                <Checkbox checked={autoGenerate} onChange={toggleAutoGenerate} />
                <CheckboxLabel>Auto-generate Client ID and Secret</CheckboxLabel>
            </CheckboxRow>
            {autoGenerate && <>
                <FieldLine>
                    <ReadOnlyField>
                        <ReadOnlyLabel>Client ID</ReadOnlyLabel>
                        <ReadOnlyValue>{clientIdValue}</ReadOnlyValue>
                    </ReadOnlyField>
                </FieldLine>
                <FieldLine>
                    <ReadOnlyField>
                        <ReadOnlyLabel>Secret</ReadOnlyLabel>
                        <ReadOnlyValue>{secretValue}</ReadOnlyValue>
                    </ReadOnlyField>
                </FieldLine>
            </>}
            {!autoGenerate && <>
                <FieldLine>
                    <TextInput margin="10px 10px 20px 10px" value={manualClientId} onChange={(e) => setManualClientId(e.currentTarget.value)} label="Client ID" />
                </FieldLine>
                <FieldLine>
                    <TextInput margin="10px 10px 20px 10px" value={manualSecret} onChange={(e) => setManualSecret(e.currentTarget.value)} label="Secret" />
                </FieldLine>
            </>}
            <StatusContainer>
                {error && <ErrorBox>{error}</ErrorBox>}
            </StatusContainer>
            <DialogActions>
                <Button outlined={true} onClick={closeDialog}>Cancel</Button>
                <Button onClick={addAgentConfirm} margin="0px 16px 0px 16px">Confirm</Button>
            </DialogActions>
        </FieldsContainer>}
        {success && <MainContainer>
            <SuccessDetails>
                <SuccessMessage>Agent created successfully</SuccessMessage>
                <WarningMessage>Make sure to copy the credentials below.</WarningMessage>
                <ReadOnlyField>
                    <ReadOnlyLabel>Client ID</ReadOnlyLabel>
                    <CopyRow>
                        <ReadOnlyValue style={{ width: "100%" }}>{clientIdValue}</ReadOnlyValue>
                        <CopyIcon onClick={() => copyToClipboard(clientIdValue, setCopiedClientId)} title="Copy to clipboard">
                            <FontAwesomeIcon icon={copiedClientId ? faCheck : faCopy} style={{ color: copiedClientId ? "#4f7326" : undefined }} />
                        </CopyIcon>
                    </CopyRow>
                </ReadOnlyField>
                <ReadOnlyField>
                    <ReadOnlyLabel>Secret</ReadOnlyLabel>
                    <CopyRow>
                        <ReadOnlyValue style={{ width: "100%" }}>{secretValue}</ReadOnlyValue>
                        <CopyIcon onClick={() => copyToClipboard(secretValue, setCopiedSecret)} title="Copy to clipboard">
                            <FontAwesomeIcon icon={copiedSecret ? faCheck : faCopy} style={{ color: copiedSecret ? "#4f7326" : undefined }} />
                        </CopyIcon>
                    </CopyRow>
                </ReadOnlyField>
            </SuccessDetails>
            <Button onClick={closeDialog} margin="16px 16px 0px 16px">Close</Button>
        </MainContainer>}
    </Dialog>;
};

export default CreateAgentDialog;
